import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, ArrowUpRight, Monitor, Smartphone, Globe, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PROJECTS } from '../constants';
import { Project } from '../types';
import { useLanguage } from '../context/LanguageContext';

const Portfolio: React.FC = () => {
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const { t, isUrdu } = useLanguage();

  const categories = [
    { key: 'All', label: t.portfolio.filterAll },
    { key: 'Development', label: t.portfolio.filterDev },
    { key: 'WordPress', label: t.portfolio.filterWp },
    { key: 'Design', label: t.portfolio.filterDesign },
  ];

  const scrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedProject) {
        setSelectedProject(null);
      }
    };

    if (selectedProject) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedProject]);

  const openModal = (project: Project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  const filteredProjects = PROJECTS.filter(project => {
    const matchesCategory = filter === 'All' || project.category === filter;
    const matchesSearch = searchQuery.trim() === '' || 
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tech.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <section 
        id="portfolio" 
        aria-label="Featured Projects & Web Development Portfolio"
        className="py-14 sm:py-20 bg-white scroll-mt-24 relative overflow-hidden"
      >
        <div className="w-full px-6 lg:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-center lg:items-end gap-10 mb-16 text-center lg:text-left">
            <div className="max-w-2xl">
              <div className="inline-block px-4 py-1.5 bg-slate-100 border border-slate-200 rounded-full text-slate-600 text-[10px] font-bold uppercase tracking-[0.3em] mb-6 shadow-sm">
                {t.portfolio.badge}
              </div>
              <h2 className="text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-900">
                {t.portfolio.title}
              </h2>
            </div>
            
            <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4">
              <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label="Project Categories">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    role="tab"
                    aria-selected={filter === cat.key}
                    aria-controls="project-grid"
                    onClick={() => setFilter(cat.key)}
                    className={`px-6 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                      filter === cat.key 
                      ? 'bg-slate-900 text-white shadow-md scale-105' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 border border-slate-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <a 
                href="https://www.behance.net/Taibegraphics" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 bg-slate-50 text-slate-600 hover:bg-slate-700 hover:text-white border border-slate-100 flex items-center gap-2 shadow-sm"
              >
                {isUrdu ? 'گرافک پورٹ فولیو' : 'Graphic Portfolio'} <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Interactive Search Bar */}
          <div className="max-w-md mx-auto mb-16 relative group px-6 sm:px-0">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isUrdu ? "پروجیکٹس تلاش کریں (جیسے ورڈپریس، ری ایکٹ)..." : "Search projects (e.g., WordPress, React, PHP)..."}
                className="w-full bg-slate-50 border border-slate-200 rounded-full px-6 py-4 pl-12 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-600 focus:bg-white transition-all shadow-sm"
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 text-xs font-bold uppercase tracking-wider bg-slate-200/80 px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                >
                  {isUrdu ? 'صاف کریں' : 'Clear'}
                </button>
              )}
            </div>
          </div>

          {/* Project Grid / Slider */}
          <div className="relative">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-20 bg-slate-50 rounded-[2.5rem] border border-slate-200 max-w-xl mx-auto shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-50 text-slate-600 mb-6 border border-slate-100">
                  <Globe size={24} className="animate-pulse" />
                </div>
                <h4 className="text-xl font-display font-bold text-slate-900 mb-2">
                  {isUrdu ? 'کوئی پروجیکٹ نہیں ملا' : 'No Matching Projects Found'}
                </h4>
                <p className="text-slate-600 text-xs sm:text-sm max-w-md mx-auto mb-8 px-6">
                  {isUrdu ? 'آپ کی تلاش کے مطابق کوئی پروجیکٹ نہیں ہے۔ فلٹر ری سیٹ کریں۔' : 'There are no archived projects matching your search term. Reset your filter to view all developments.'}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilter('All');
                  }}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer"
                >
                  {isUrdu ? 'فلٹرز ری سیٹ کریں' : 'Reset Filters'}
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Grid */}
                <div id="project-grid" className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredProjects.map((project) => (
                    <div 
                      key={project.id} 
                      role="button"
                      aria-label={`View details for ${project.title}`}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(project); }}
                      className="group relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-slate-100 border border-slate-200 transition-all duration-300 hover:-translate-y-2 hover:border-slate-300 hover:shadow-xl cursor-pointer reveal will-change-transform shadow-sm"
                      onClick={() => openModal(project)}
                    >
                      {/* Project Image */}
                      <img 
                        src={project.image} 
                        alt={project.title}
                        loading="lazy"
                        width="400"
                        height="500"
                        decoding="async"
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                      />
                      
                      {/* Gradient Mask */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent transition-opacity"></div>
                      
                      {/* Info Panel */}
                      <div className="absolute inset-0 flex flex-col justify-end p-7 sm:p-8">
                        <div className="space-y-4">
                          <div className="flex flex-wrap gap-2">
                            {project.tech.map((tItem, i) => (
                              <span key={i} className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-md text-[9px] font-bold text-white uppercase tracking-wider border border-white/25">
                                {tItem}
                              </span>
                            ))}
                          </div>
                          
                          <h3 className="text-2xl font-display font-bold text-white group-hover:text-slate-100 transition-colors leading-tight">
                            {project.title}
                          </h3>
                          
                          <div className="flex items-center justify-between pt-3 border-t border-white/20">
                            {project.liveUrl ? (
                              <a 
                                href={project.liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-bold text-[11px] uppercase tracking-wider shadow-sm transition-all duration-200 cursor-pointer"
                              >
                                <span>{t.portfolio.viewLive}</span>
                                <ArrowUpRight size={14} className="text-indigo-600" />
                              </a>
                            ) : (
                              <span className="text-white/60 text-xs font-semibold">
                                {isUrdu ? 'تفصیلات دیکھیں' : 'Click for Details'}
                              </span>
                            )}
                            <div className="flex gap-2.5 text-white/80 bg-black/30 backdrop-blur-md px-2.5 py-1.5 rounded-lg border border-white/10">
                              <Monitor size={15} />
                              <Smartphone size={15} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Scrollable Slider with Navigation Controls */}
                <div className="md:hidden relative -mx-6 px-6">
                  {/* Top-Center Navigation Controls */}
                  <div className="flex flex-col items-center justify-center gap-1.5 mb-4">
                    <div className="flex items-center justify-center gap-4 bg-slate-900/90 backdrop-blur-md text-white px-5 py-2.5 rounded-full shadow-lg border border-slate-800">
                      <button
                        onClick={() => scrollSlider('left')}
                        className="p-2 rounded-full bg-slate-800 hover:bg-slate-600 active:scale-90 text-white transition-all cursor-pointer flex items-center justify-center shadow-xs"
                        aria-label="Previous project"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <div className="flex items-center gap-1.5 px-1">
                        <span className="text-[11px] font-mono font-extrabold text-slate-400">
                          {filteredProjects.length}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest font-extrabold text-slate-300">
                          {isUrdu ? 'پروجیکٹس' : 'Projects'}
                        </span>
                      </div>
                      <button
                        onClick={() => scrollSlider('right')}
                        className="p-2 rounded-full bg-slate-800 hover:bg-slate-600 active:scale-90 text-white transition-all cursor-pointer flex items-center justify-center shadow-xs"
                        aria-label="Next project"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <span className="text-[10px] text-slate-500 font-medium tracking-wide">
                      {isUrdu ? 'دیکھنے کے لیے تیر پر کلک کریں' : 'Click arrows to browse work'}
                    </span>
                  </div>

                  <div 
                    ref={sliderRef}
                    className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-5 py-2 pb-6" 
                    role="list" 
                    aria-label="Project slider"
                  >
                    {filteredProjects.map((project, index) => (
                      <div 
                        key={`${project.id}-${index}`} 
                        role="listitem"
                        aria-label={`View details for ${project.title}`}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(project); }}
                        className="flex-shrink-0 w-[280px] snap-center group relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-slate-100 border border-slate-200 transition-all duration-300 cursor-pointer shadow-md will-change-transform"
                        onClick={() => openModal(project)}
                      >
                        {/* Project Image */}
                        <img 
                          src={project.image} 
                          alt={project.title}
                          loading="lazy"
                          width="280"
                          height="350"
                          decoding="async"
                          className="w-full h-full object-cover transition-all duration-700"
                        />
                        
                        {/* Gradient Mask */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent"></div>
                        
                        {/* Info Panel */}
                        <div className="absolute inset-0 flex flex-col justify-end p-6">
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-1.5">
                              {project.tech.slice(0, 2).map((tItem, i) => (
                                <span key={i} className="px-2.5 py-1 bg-white/20 backdrop-blur-md rounded-md text-[8px] font-bold text-white uppercase tracking-wider border border-white/20">
                                  {tItem}
                                </span>
                              ))}
                            </div>
                            
                            <h4 className="text-xl font-display font-bold text-white leading-tight">
                              {project.title}
                            </h4>
                            
                            <div className="flex items-center justify-between pt-3 border-t border-white/20">
                              {project.liveUrl ? (
                                <a 
                                  href={project.liveUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-900 font-bold text-[10px] uppercase tracking-wider shadow-sm cursor-pointer"
                                >
                                  <span>{t.portfolio.viewLive}</span>
                                  <ArrowUpRight size={12} className="text-indigo-600" />
                                </a>
                              ) : (
                                <span className="text-white/70 text-[10px] font-semibold">
                                  {isUrdu ? 'تفصیلات دیکھیں' : 'View Details'}
                                </span>
                              )}
                              <div className="flex gap-2 text-white/80">
                                <Monitor size={14} />
                                <Smartphone size={14} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Project Modal Portal - mounted on document.body for pristine viewport positioning */}
      {selectedProject && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 overflow-y-auto bg-slate-950/75 backdrop-blur-md transition-opacity animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-modal-title"
          onClick={closeModal}
        >
          <div 
            className="relative w-full max-w-4xl bg-white border border-slate-200 rounded-3xl sm:rounded-[2.5rem] overflow-hidden shadow-2xl z-10 flex flex-col md:flex-row max-h-[90vh] my-auto"
            onClick={(e) => e.stopPropagation()}
            dir={isUrdu ? 'rtl' : 'ltr'}
          >
            {/* Close Button */}
            <button 
              onClick={closeModal}
              aria-label="Close project details"
              className="absolute top-4 right-4 z-30 w-10 h-10 bg-slate-900/90 hover:bg-slate-900 text-white rounded-full flex items-center justify-center transition-all shadow-lg cursor-pointer border border-white/20"
            >
              <X size={20} aria-hidden="true" />
            </button>
            
            {/* Project Image Preview */}
            <div className="w-full md:w-1/2 h-64 md:h-auto min-h-[260px] relative overflow-hidden bg-slate-900 shrink-0">
              <img 
                src={selectedProject.image} 
                alt={selectedProject.title}
                width="800"
                height="1000"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent md:hidden" />
            </div>
            
            {/* Project Details Content */}
            <div className="w-full md:w-1/2 p-6 sm:p-10 flex flex-col justify-between overflow-y-auto bg-white">
              <div>
                <div className="inline-block px-3.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200/80 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 w-fit">
                  {selectedProject.category}
                </div>
                <h3 id="project-modal-title" className="text-2xl sm:text-3xl font-display font-extrabold text-slate-900 mb-3 leading-tight">
                  {selectedProject.title}
                </h3>
                <p className={`text-slate-600 text-xs sm:text-sm leading-relaxed mb-6 font-normal ${isUrdu ? 'font-urdu' : ''}`}>
                  {selectedProject.description}
                </p>
                
                <div className="mb-6">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">
                    {isUrdu ? 'استعمال شدہ ٹیکنالوجیز' : 'Technologies Used'}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tech.map((tItem, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-100 rounded-lg text-xs font-bold text-slate-700 border border-slate-200">
                        {tItem}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                {selectedProject.liveUrl ? (
                  <a 
                    href={selectedProject.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-md w-full text-center cursor-pointer"
                  >
                    <span>{t.portfolio.viewLive}</span>
                    <ExternalLink size={15} />
                  </a>
                ) : (
                  <button
                    onClick={closeModal}
                    className="px-6 py-3.5 bg-slate-900 text-white rounded-xl font-bold text-xs uppercase tracking-wider w-full"
                  >
                    {isUrdu ? 'بند کریں' : 'Close'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Portfolio;
